import React, {useEffect, useCallback} from 'react';
import {IconButton, Typography, HelpCircleIcon} from '@customink/pigment-react';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import LayeringPills from '~/components/LayeringPills';
import {useFlagVariation} from '@customink/signalmizely-react';

import './index.scss';
import {useSignalmizelyFlagEnabled} from '~/adapters/signalmizely/hooks';
import {pushGtmEvent} from '../../utils/analytics';

const classHeader = 'ndx-MobileBottomToolbar';

const MobileBottomToolbar = () => {
  const trackHelpIconClick = useCallback(() => {
    pushGtmEvent({
      event: 'lab_interaction',
      click_section: 'help',
      click_link: 'icon'
    });
  }, []);
  const openHelpDialog = useCallback(
    (e: React.MouseEvent) => {
      const ciDialog = document.querySelector('.customer-help ci-dialog') as {
        openModal: (e: React.MouseEvent) => void;
      } | null;
      if (ciDialog) {
        ciDialog.openModal(e);
        trackHelpIconClick();
      }
    },
    [trackHelpIconClick]
  );
  const trackCustomerChatClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    pushGtmEvent({
      event: 'lab_interaction',
      click_section: 'help',
      click_link: 'chat'
    });
  }, []);
  const trackCustomerContactClick = useCallback(() => {
    pushGtmEvent({
      event: 'lab_interaction',
      click_section: 'help',
      click_link: 'phone'
    });
  }, []);

  const HELP_ICON_RELOCATION_FLAG = 'ndx_help_icon_relocation_on_mobile';
  const isEnabled = useSignalmizelyFlagEnabled(HELP_ICON_RELOCATION_FLAG);
  const {variation, isLoading} = useFlagVariation(HELP_ICON_RELOCATION_FLAG);
  useEffect(() => {
    if (isLoading) {
      return;
    }
    pushGtmEvent({
      event: 'ab_test_group',
      ab_test_name: '2025 07 24 help icon relocation on mobile',
      ab_test_location: 'lab',
      ab_test_group: variation
    });
  }, [variation, isLoading]);
  useEffect(() => {
    if (isEnabled) {
      document.querySelector('ci-header')?.classList.add('ndx-help-relocation');
    }
  }, [isEnabled]);
  useEffect(() => {
    const helpButton = document.querySelector(
      '.customer-help #customer-btn'
    ) as HTMLElement | null;
    const customerChat = document.querySelector(
      '.customer-help ci-dialog customer-chat'
    ) as HTMLElement | null;
    const customerContact = document.querySelector(
      '.customer-help ci-dialog customer-contact'
    ) as HTMLElement | null;
    customerChat?.addEventListener('click', trackCustomerChatClick);
    customerContact?.addEventListener('click', trackCustomerContactClick);
    helpButton?.addEventListener('click', trackHelpIconClick);
    return () => {
      helpButton?.removeEventListener('click', trackHelpIconClick);
      customerChat?.removeEventListener('click', trackCustomerChatClick);
      customerContact?.removeEventListener('click', trackCustomerContactClick);
    };
  }, [trackCustomerChatClick, trackCustomerContactClick, trackHelpIconClick]);

  return (
    <>
      {isEnabled && (
        <IconButton
          fill
          size="large"
          onClick={openHelpDialog}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:active': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            },
            bottom: '70px',
            position: 'absolute',
            right: '8px',
            width: '60px',
            height: '60px'
          }}>
          <HelpCircleIcon
            sx={{
              color: designTokens.color.shade.white.value
            }}
          />
          <Typography
            variant="uiTextTiny"
            sx={{
              color: designTokens.color.shade.white.value
            }}>
            Help
          </Typography>
        </IconButton>
      )}
      <div className={`${classHeader} u-clickPassThrough`}>
        <LayeringPills />
      </div>
    </>
  );
};

MobileBottomToolbar.displayName = 'MobileBottomToolbar';

export default MobileBottomToolbar;
